<script setup lang="ts">
import { computed, ref } from 'vue';
import ProjectCatalogGrid from '~/components/project-catalog/ProjectCatalogGrid.vue';
import ProjectCatalogList from '~/components/project-catalog/ProjectCatalogList.vue';
import Pagination from '~/components/Pagination.vue';
import type { Project } from '~/utilities/pyscript-api-models';
import { useListGridViewPreference } from '~/composables/list-grid-view-preference';
import type { ProjectPagination } from '~/composables/paginated-project-list';

const props = defineProps<{
  projectList: Project[];
  pagination?: ProjectPagination;
  setPage?: (value: number) => void;
  tagClickHandler: (...args: any) => void;
  /** Used to refetch the project list when a project is deleted or updated. */
  refetch: () => Promise<void>;
}>();

const emit = defineEmits<{
  (e: 'projectDeleted', value: Project): void;
  (e: 'projectUpdated', value: Project): void;
}>();

const { savedView } = useListGridViewPreference();

const projectWasDeleted = ref(false);

async function onProjectDeleted(value: Project) {
  projectWasDeleted.value = true;
  await props.refetch();
  // Gives time for the leave animation to finish.
  setTimeout(() => (projectWasDeleted.value = false), 500);
  emit('projectDeleted', value);
}

async function onProjectUpdated(value: Project) {
  await props.refetch();
  emit('projectUpdated', value);
}

const itemsRange = computed(() => {
  if (!props.pagination) return;

  const { page, page_size, total_count, total_pages } = props.pagination;
  return {
    start: page * page_size - (page_size - 1),
    end: page === total_pages ? total_count : page * page_size,
    total: total_count,
  };
});
</script>

<template>
  <section>
    <ProjectCatalogGrid
      v-if="savedView === 'grid'"
      :project-list="projectList"
      :animate-leave="projectWasDeleted"
      :tag-click-handler="tagClickHandler"
      @project-deleted="onProjectDeleted"
      @project-updated="onProjectUpdated"
    />

    <ProjectCatalogList
      v-else
      :project-list="projectList"
      :animate-leave="projectWasDeleted"
      :tag-click-handler="tagClickHandler"
      @project-deleted="onProjectDeleted"
      @project-updated="onProjectUpdated"
    />

    <div
      v-if="pagination && itemsRange && setPage && itemsRange.end > 0"
      class="mt-5 flex flex-col items-center gap-3"
    >
      <div class="text-center text-xs">
        Showing <span class="font-medium">{{ itemsRange.start }}</span> to
        <span class="font-medium">{{ itemsRange.end }}</span> of
        <span class="font-medium">{{ itemsRange.total }}</span> results
      </div>

      <Pagination
        :current-page="pagination.page"
        :total-pages="pagination.total_pages"
        :total-count="pagination.total_count"
        :has-next-page="Boolean(pagination.next_page)"
        :has-previous-page="Boolean(pagination.previous_page)"
        :set-page="setPage"
      />
    </div>
  </section>
</template>
